import React, { useMemo, useState } from "react";

import axios from "axios";
import type { AxiosError } from "axios";
import { styled } from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

import { describeQueryFallback } from "./describeQuery";
import type { FilterDescription, SavedView } from "./FilterModalProps";
import ViewSummary from "./ViewSummary";
import Button from "../../Button/Button";
import StatusPill from "../../StatusPill/StatusPill";
import Switch from "../../Switch/Switch";
import {
  createTableV2ColumnHelper,
  expanderColumn,
} from "../../TableV2/columns";
import TableV2Grid from "../../TableV2/components/TableV2Grid";
import TableV2Pagination from "../../TableV2/components/TableV2Pagination";
import { useTableV2 } from "../../TableV2/hooks/useTableV2";
import { TableFrame } from "../../TableV2/styles";
import { Toast } from "../../Toast/Toast";

const PAGE_SIZE = 10;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${gridSpacing[2]}px;
`;

const Expanded = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gridSpacing[3]}px;
`;

const Description = styled.div`
  font-size: 14px;
  color: ${colorTheme("neutral")};
`;

type ListedView = SavedView & { uuid: string };

const columnHelper = createTableV2ColumnHelper<ListedView>();

// A 400 carries the API's validation message, e.g. a public view already using the name
const shareError = (err: AxiosError<{ result?: unknown }>) => {
  const result = err.response?.data?.result;
  if (err.response?.status !== 400 || !result) {
    return "Something went wrong changing who can see this view";
  }
  if (typeof result === "string") return result;
  return Object.values(result as Record<string, unknown>)
    .flat()
    .filter((message) => typeof message === "string")
    .join(" ");
};

const filterSummary = (filters: FilterDescription[]) =>
  filters
    .filter((filter) => filter.values.length)
    .map((filter) => `${filter.label}: ${filter.values.join(", ")}`)
    .join(" · ") || "No filters";

const SavedViewsTable = ({
  views,
  username,
  openSavedFilter,
  removeFilter,
  updateFilter,
  describeQuery = describeQueryFallback,
  describeLayout,
}: {
  views: SavedView[];
  username: string;
  openSavedFilter: (view: SavedView) => void;
  removeFilter: (view: SavedView) => void;
  updateFilter?: (view: SavedView) => void;
  describeQuery?: (query: string) => FilterDescription[];
  describeLayout?: (layout: object) => string[];
}) => {
  const [busy, setBusy] = useState<Record<string, "sharing" | "deleting">>({});
  const listed = useMemo(
    () => views.filter((view): view is ListedView => !!view.uuid),
    [views],
  );

  const setRowBusy = (uuid: string, state?: "sharing" | "deleting") =>
    setBusy((current) => {
      const next = { ...current };
      if (state) next[uuid] = state;
      else delete next[uuid];
      return next;
    });

  const toggleShared = (view: ListedView) => {
    setRowBusy(view.uuid, "sharing");
    axios
      .put(`search/filters/${view.uuid}`, { public: !view.public })
      .then((res) => updateFilter?.(res.data.result))
      .catch((err) => {
        console.error(err);
        Toast({ type: "error", message: shareError(err) });
      })
      .finally(() => setRowBusy(view.uuid));
  };

  const remove = (view: ListedView) => {
    setRowBusy(view.uuid, "deleting");
    axios
      .delete(`search/filters/${view.uuid}`)
      .then(() => removeFilter(view))
      .catch((err) => {
        console.error(err);
        Toast({
          type: "error",
          message: "Something went wrong deleting the view",
        });
      })
      .finally(() => setRowBusy(view.uuid));
  };

  const columns = useMemo(
    () => [
      expanderColumn<ListedView>(),
      columnHelper.accessor("name", {
        header: "Name",
        size: 200,
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor(
        (view) => filterSummary(describeQuery(view.query)),
        {
          id: "filters",
          header: "Filters",
          size: 260,
          enableSorting: false,
        },
      ),
      // Only tables that save layouts can tell views apart by what they save
      ...(describeLayout
        ? [
            columnHelper.accessor(
              (view) =>
                view.table_state ? "Filters + table layout" : "Filters only",
              {
                id: "saves",
                header: "Saves",
                size: 170,
                cell: ({ getValue, row }) => (
                  <StatusPill
                    small
                    type={row.original.table_state ? "primary" : "neutral"}
                    text={getValue()}
                  />
                ),
              },
            ),
          ]
        : []),
      columnHelper.accessor((view) => (view.public ? "Public" : "Private"), {
        id: "sharing",
        header: "Sharing",
        size: 110,
        cell: ({ row: { original: view } }) =>
          view.created_by_username === username ? (
            <Switch
              label="Public"
              checked={!!view.public}
              disabled={!!busy[view.uuid]}
              onChange={() => toggleShared(view)}
              testId={`view-public-${view.uuid}`}
            />
          ) : (
            <StatusPill
              small
              type={view.public ? "success" : "neutral"}
              text={view.public ? "Public" : "Private"}
            />
          ),
      }),
      columnHelper.accessor("created_by_name", {
        header: "Created by",
        size: 140,
      }),
      columnHelper.accessor("modified", {
        header: "Saved",
        size: 130,
        meta: { type: "date" },
      }),
      columnHelper.display({
        id: "apply",
        size: 100,
        enableSorting: false,
        enableResizing: false,
        meta: { align: "right", label: "Apply" },
        cell: ({ row: { original: view } }) => (
          <Button
            small
            variant="secondary"
            text="Apply"
            disabled={busy[view.uuid] === "deleting"}
            testId={`view-apply-${view.uuid}`}
            onClick={() => openSavedFilter(view)}
          />
        ),
      }),
    ],
    [username, busy, describeQuery, describeLayout],
  );

  const table = useTableV2({
    data: listed,
    columns,
    getRowId: (view) => view.uuid,
    getRowCanExpand: () => true,
    enableRowSelection: false,
    initialState: {
      pagination: { pageIndex: 0, pageSize: PAGE_SIZE },
      sorting: [{ id: "name", desc: false }],
    },
  });

  return (
    <TableFrame>
      <TableV2Grid
        table={table}
        columnMenu={false}
        columnDrag={false}
        stableHeight={false}
        emptyMessage="No saved views found"
        renderSubComponent={({ original: view }) => (
          <Expanded>
            {view.description && <Description>{view.description}</Description>}
            <ViewSummary
              query={view.query}
              tableState={view.table_state}
              describeQuery={describeQuery}
              describeLayout={describeLayout}
            />
            {view.created_by_username === username && (
              <Actions>
                <Button
                  small
                  variant="secondary"
                  status={
                    busy[view.uuid] === "deleting" ? "retrieving" : "ready"
                  }
                  icon="fa-trash"
                  disabled={!!busy[view.uuid]}
                  testId={`view-delete-${view.uuid}`}
                  onClick={() => remove(view)}
                />
              </Actions>
            )}
          </Expanded>
        )}
      />
      {listed.length > PAGE_SIZE && <TableV2Pagination table={table} />}
    </TableFrame>
  );
};

export default SavedViewsTable;
